import { useState } from 'react'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'
import {
  fromLocalInputValue,
  setVideoVisibleFrom,
  toLocalInputValue,
  type VideoGate,
} from '../lib/videoGate'

/**
 * The strip above the video card, for schedulers only (156, David 11 Oct).
 *
 * Hidden: "Hidden from staff until <when>" + Change time + Show it now.
 * Shown:  "Everyone can see this video" + Hide until…
 *
 * The time is entered and shown in the reader's own zone, and the zone is
 * NAMED in what is shown (rule 9: UTC stored, the zone explicit). Somebody in
 * Santiago scheduling "9:00 Pacific" types their own 13:00 and reads back a
 * time with its zone, rather than guessing which clock the box meant.
 */
export function VideoSchedule({
  gate,
  locale,
  onChanged,
}: {
  gate: VideoGate
  locale: string
  onChanged: () => void
}) {
  const h = HOME[useHubLang()]
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(() =>
    toLocalInputValue(gate.visibleFrom ?? nextNine()),
  )
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  const when = gate.visibleFrom ? formatWhen(gate.visibleFrom, locale) : null

  async function save(at: Date | null) {
    setBusy(true)
    setFailed(false)
    try {
      await setVideoVisibleFrom(at)
      setEditing(false)
      onChanged()
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`videosched${gate.shown ? '' : ' videosched--hidden'}`} role="status">
      {!editing ? (
        <>
          <p className="videosched__line">
            {gate.shown || !when ? h.videoShownToAll : h.videoHiddenUntil(when)}
          </p>
          <div className="videosched__actions">
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setEditing(true)}>
              {gate.shown ? h.videoHideUntil : h.videoChangeTime}
            </button>
            {!gate.shown && (
              <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={() => save(null)}>
                {h.videoShowNow}
              </button>
            )}
          </div>
        </>
      ) : (
        <form
          className="videosched__form"
          onSubmit={(e) => {
            e.preventDefault()
            const at = fromLocalInputValue(value)
            if (at) void save(at)
          }}
        >
          <label className="videosched__label">
            {h.videoShowFrom}
            <input
              type="datetime-local"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </label>
          <div className="videosched__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !fromLocalInputValue(value)}>
              {h.videoSave}
            </button>
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setEditing(false)}>
              {h.videoCancel}
            </button>
          </div>
        </form>
      )}
      {failed && <p className="videosched__failed" role="alert">{h.videoSaveFailed}</p>}
    </div>
  )
}

/** Tomorrow 9:00 local — the likeliest thing anybody types into this box. */
function nextNine(): Date {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return d
}

export function formatWhen(d: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(d)
  } catch {
    return d.toISOString()
  }
}
